import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AdminHomeButton from "../components/AdminHomeButton";
import AdminPageNavigator from "../components/AdminPageNavigator";
import SiteFooter from "../components/SiteFooter";
import { API_BASE_URL } from "../config/apiBaseUrl";
import { persistAdminWorkSessionId } from "../utils/adminWorkSessionStorage";

type SubmissionRow = {
  storyId: string;
  sessionId: string;
  email: string | null;
  country: string | null;
  submittedAt: string | null;
  submissionStatus?: string | null;
  storyImagesLastSentAt?: string | null;
  digitalBookSentForReviewAt?: string | null;
  digitalBookPagePublishedAt?: string | null;
  adminComments?: string | null;
};

const SUBMISSION_STATUSES = [
  "Story Submitted",
  "Story In Progress",
  "Finising Touches",
  "Your Story Is Ready",
  "Required Changes Submitted",
] as const;

type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

const formatDateTime = (value: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

const AdminSubmissions: React.FC = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();

  const isAdmin = user?.isAdmin === true;

  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openingSessionId, setOpeningSessionId] = useState<string | null>(null);
  const [savingStatusBySessionId, setSavingStatusBySessionId] = useState<Record<string, boolean>>({});
  const [commentsModalSessionId, setCommentsModalSessionId] = useState<string | null>(null);
  const [commentsDraft, setCommentsDraft] = useState("");
  const [savingComments, setSavingComments] = useState(false);
  const commentsTextareaRef = useRef<HTMLTextAreaElement>(null);

  const headers = useMemo(() => ({ ...getAuthHeader() }), [getAuthHeader]);

  const commentsModalRow = useMemo(
    () => (commentsModalSessionId ? rows.find((r) => r.sessionId === commentsModalSessionId) ?? null : null),
    [commentsModalSessionId, rows]
  );

  const openCommentsModal = (sessionId: string, initialText: string) => {
    setCommentsModalSessionId(sessionId);
    setCommentsDraft(initialText ?? "");
  };

  const closeCommentsModal = useCallback(() => {
    setCommentsModalSessionId(null);
    setCommentsDraft("");
    setSavingComments(false);
  }, []);

  useEffect(() => {
    if (!commentsModalSessionId) return;
    const t = window.setTimeout(() => commentsTextareaRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [commentsModalSessionId]);

  useEffect(() => {
    if (!commentsModalSessionId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCommentsModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [commentsModalSessionId, closeCommentsModal]);

  const handleSaveComments = async () => {
    if (!commentsModalSessionId) return;
    setError(null);
    setSavingComments(true);
    const sessionId = commentsModalSessionId;
    const textToSave = commentsDraft;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/submissions/${encodeURIComponent(sessionId)}/comments`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ adminComments: textToSave }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || "Failed to save comments");
      const saved = typeof data?.data?.adminComments === "string" ? data.data.adminComments : textToSave;
      setRows((current) => current.map((r) => (r.sessionId === sessionId ? { ...r, adminComments: saved } : r)));
      closeCommentsModal();
    } catch (e: any) {
      setError(e?.message || "Failed to save comments");
    } finally {
      setSavingComments(false);
    }
  };

  useEffect(() => {
    if (user && !isAdmin) {
      navigate("/create/order-summary", { replace: true });
    }
  }, [user, isAdmin, navigate]);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/submissions`, { headers });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || "Failed to load submissions");
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (e: any) {
      setError(e?.message || "Failed to load submissions");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleWorkOnStory = async (sessionId: string) => {
    setOpeningSessionId(sessionId);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/submissions/${encodeURIComponent(sessionId)}/open`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || "Failed to open submission");

      const openedSessionId = typeof data?.data?.sessionId === "string" ? data.data.sessionId : sessionId;
      persistAdminWorkSessionId(openedSessionId);

      const lastStep = data?.data?.adminLastStep || "studio";
      const stepToPath: Record<string, string> = {
        studio: "/create/story-studio",
        "image-studio": "/create/image-studio",
        result: "/create/result",
      };
      const target = stepToPath[lastStep] || "/create/story-studio";
      navigate(`${target}?sessionId=${encodeURIComponent(sessionId)}`);
    } catch (e: any) {
      setError(e?.message || "Failed to open submission");
    } finally {
      setOpeningSessionId(null);
    }
  };

  const handleStatusChange = async (sessionId: string, nextStatus: SubmissionStatus) => {
    setError(null);
    setSavingStatusBySessionId((prev) => ({ ...prev, [sessionId]: true }));

    const prevRows = rows;
    setRows((current) =>
      current.map((r) => (r.sessionId === sessionId ? { ...r, submissionStatus: nextStatus } : r))
    );

    try {
      const res = await fetch(`${API_BASE_URL}/admin/submissions/${encodeURIComponent(sessionId)}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ submissionStatus: nextStatus }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || "Failed to update status");

      const saved = (data?.data?.submissionStatus as string | undefined) || nextStatus;
      setRows((current) =>
        current.map((r) => (r.sessionId === sessionId ? { ...r, submissionStatus: saved } : r))
      );
    } catch (e: any) {
      setRows(prevRows);
      setError(e?.message || "Failed to update status");
    } finally {
      setSavingStatusBySessionId((prev) => ({ ...prev, [sessionId]: false }));
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden px-4 py-8">
      <AdminHomeButton className="fixed top-4 right-4 z-50 bg-white/85 hover:bg-white text-heading border border-white/40 font-semibold px-4 py-2 rounded-2xl shadow-soft hover:shadow-card transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2" />
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/wizard/submission%20List.png')" }}
      />
      <div className="absolute inset-0 bg-brandBeige/40" />

      <main id="main-content" tabIndex={-1} className="relative z-10 max-w-5xl mx-auto outline-none pb-8">
        <div className="mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-heading">Submitted Stories</h1>
            <AdminPageNavigator align="start" />
            <p className="text-body mt-1">Select a user to resume work on their submitted story.</p>
          </div>
        </div>

        {error && (
          <div role="alert" aria-live="polite" className="bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded-card shadow-soft mb-4">
            {error}
          </div>
        )}

        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={load}
            disabled={isLoading}
            className="bg-brandNavy/90 hover:bg-brandNavy text-white font-semibold py-2.5 px-5 rounded-2xl shadow-soft disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-card border border-white/40 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[2020px]">
              <div className="grid grid-cols-[80px_170px_minmax(260px,2fr)_minmax(220px,1.2fr)_200px_200px_200px_220px_minmax(140px,1fr)_140px] gap-3 px-5 py-4 border-b border-gray-200 text-sm font-semibold text-gray-700">
                <div>Work</div>
                <div>View Digital Book</div>
                <div>Email</div>
                <div>Submitted</div>
                <div>Sent for review</div>
                <div>Pictures emailed</div>
                <div>Digital book page</div>
                <div>Status</div>
                <div>Country</div>
                <div>Comments</div>
              </div>
            </div>

            {isLoading ? (
              <div className="p-6 text-body">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-body">No submissions yet.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <div key={r.storyId} className="min-w-[2020px] grid grid-cols-[80px_170px_minmax(260px,2fr)_minmax(220px,1.2fr)_200px_200px_200px_220px_minmax(140px,1fr)_140px] gap-3 px-5 py-4 items-center">
                    <div className="flex flex-col gap-1.5 items-start">
                      <button
                        type="button"
                        onClick={() => handleWorkOnStory(r.sessionId)}
                        disabled={openingSessionId === r.sessionId || savingStatusBySessionId[r.sessionId] === true}
                        className="w-14 bg-brandOrange hover:bg-orange-600 text-white font-semibold px-3 py-1.5 rounded-xl shadow-soft text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {openingSessionId === r.sessionId ? "..." : "Work"}
                      </button>
                    </div>
                    <div className="flex flex-col gap-1.5 items-start">
                      <button
                        type="button"
                        title="Open digital book viewer"
                        onClick={() => {
                          persistAdminWorkSessionId(r.sessionId);
                          navigate(
                            `/create/digital-book?sessionId=${encodeURIComponent(r.sessionId)}&p=1`
                          );
                        }}
                        disabled={savingStatusBySessionId[r.sessionId] === true}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 rounded-xl shadow-soft text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        View Digital Book
                      </button>
                    </div>
                    <div className="text-heading font-medium truncate">{r.email || "—"}</div>
                    <div className="text-body whitespace-nowrap">{formatDateTime(r.submittedAt)}</div>
                    <div className="text-body text-xs whitespace-normal leading-snug">
                      {formatDateTime(r.digitalBookSentForReviewAt ?? null)}
                    </div>
                    <div className="text-body text-xs whitespace-normal leading-snug">
                      {formatDateTime(r.storyImagesLastSentAt ?? null)}
                    </div>
                    <div className="text-body text-xs whitespace-normal leading-snug">
                      {formatDateTime(r.digitalBookPagePublishedAt ?? null)}
                    </div>
                    <div>
                      <select
                        value={(r.submissionStatus || "Story Submitted") as SubmissionStatus}
                        onChange={(e) => handleStatusChange(r.sessionId, e.target.value as SubmissionStatus)}
                        disabled={savingStatusBySessionId[r.sessionId] === true}
                        className="w-[220px] shrink-0 bg-white border border-gray-300 rounded-xl px-3 py-1.5 text-xs text-heading text-center shadow-soft focus:outline-none focus:ring-2 focus:ring-brandOrange disabled:opacity-60"
                      >
                        {SUBMISSION_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="text-body truncate">{r.country || "—"}</div>
                    <div>
                      <button
                        type="button"
                        onClick={() => openCommentsModal(r.sessionId, r.adminComments ?? "")}
                        disabled={savingStatusBySessionId[r.sessionId] === true}
                        title={(r.adminComments || "").trim() ? "Edit saved comments" : "Add comments"}
                        className={`relative w-full bg-brandNavy/90 hover:bg-brandNavy text-white font-semibold py-1.5 rounded-xl shadow-soft text-xs disabled:opacity-50 disabled:cursor-not-allowed ${(r.adminComments || "").trim() ? "pl-2 pr-5" : "px-2"}`}
                      >
                        Comments
                        {(r.adminComments || "").trim() ? (
                          <span
                            className="absolute top-1.5 right-2 h-2 w-2 rounded-full bg-emerald-400"
                            aria-hidden
                          />
                        ) : null}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {commentsModalSessionId && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <button
              type="button"
              className="absolute inset-0 bg-black/45 cursor-default border-0 p-0"
              aria-label="Close dialog"
              onClick={closeCommentsModal}
              disabled={savingComments}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="admin-comments-title"
              className="relative z-10 w-full max-w-lg rounded-2xl bg-white shadow-card border border-gray-200 p-5 max-h-[min(90vh,560px)] flex flex-col"
            >
              <h2 id="admin-comments-title" className="text-lg font-bold text-heading mb-1">
                Submission comments
              </h2>
              <p className="text-xs text-gray-600 mb-2 font-mono break-all">
                {commentsModalRow?.email || commentsModalSessionId}
              </p>
              <p className="text-sm text-body mb-3">
                Notes for this row only. They are saved on the server and shown the next time you open this dialog.
                Close without saving discards unsaved edits.
              </p>
              <textarea
                ref={commentsTextareaRef}
                value={commentsDraft}
                onChange={(e) => setCommentsDraft(e.target.value)}
                maxLength={20000}
                rows={10}
                className="w-full flex-1 min-h-[200px] resize-y rounded-xl border border-gray-300 px-3 py-2 text-sm text-heading shadow-inner focus:outline-none focus:ring-2 focus:ring-brandOrange"
                placeholder="Write internal notes…"
              />
              <div className="flex justify-between items-center mt-3 gap-3 flex-wrap">
                <span className="text-xs text-gray-500">{commentsDraft.length} / 20000</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closeCommentsModal}
                    disabled={savingComments}
                    className="font-semibold px-4 py-2 rounded-xl border border-gray-300 text-heading hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveComments}
                    disabled={savingComments}
                    className="bg-brandOrange hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-xl shadow-soft disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingComments ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <SiteFooter />
      </main>
    </div>
  );
};

export default AdminSubmissions;

